import React, { useRef, useEffect } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import ScoreDashboard from './ScoreDashboard';
import EditablePromptSection from './EditablePromptSection';

const LoadingSkeleton = () => (
    <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={160} />)}
        </Box>
        <Skeleton variant="rounded" height={80} sx={{ mb: 4 }}/>
        <Skeleton variant="rounded" height={200} />
    </Box>
);


function AnalysisResults({ isLoading, result, showToast, onImprovePrompt, isImproving, improvedPrompt, onReanalyze }) {
  const analysisContainerRef = useRef(null);
  const prevIsLoadingRef = useRef();

  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current;
    // This effect triggers the scroll when the analysis is complete.
    // It checks if the previous state was loading and the current is not, and that there's a result.
    if (wasLoading && !isLoading && result) {
      // A short timeout ensures the element is painted before we try to scroll.
      setTimeout(() => {
        analysisContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, result]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!result) {
    return null; // Don't render anything if there's no result and not loading
  }

  return (
    <Box ref={analysisContainerRef} component="section" sx={{ mt: 4, scrollMarginTop: '24px' }}>
        <Box sx={{
            backgroundColor: 'hsl(50.64deg 95.61% 55.29% / 20%)',
            // Use negative horizontal margins on mobile to break out of the parent container's padding.
            mx: { xs: -2, sm: 0 },
            borderTop: '4px dashed #FAD820',
            borderBottom: '4px dashed #FAD820',
            borderLeft: { xs: 'none', sm: '4px dashed #FAD820' },
            borderRight: { xs: 'none', sm: '4px dashed #FAD820' },
            borderRadius: { xs: 0, sm: '35px' },
            overflow: 'hidden', // Ensures the inner header respects the parent's border radius
        }}>
            <Box sx={{
                backgroundColor: 'hsl(50.64deg 95.61% 55.29% / 50%)',
                textAlign: 'center',
                py: 1,
                px: { xs: 2, sm: 4 },
            }}>
                <Typography sx={{
                    fontFamily: '"VT323", monospace',
                    color: '#8e8123',
                    fontSize: { xs: '1.6rem', sm: '1.8rem' },
                }}>
                    Analyzing Natural Dialogue for You
                </Typography>
            </Box>
            {/* This inner box holds the dashboard and controls spacing */}
            <Box sx={{ px: { xs: 2, sm: 4 }, pt: { xs: 3, md: 1 }, pb: { xs: 3, sm: 4 } }}>
                <ScoreDashboard
                    axes={result.axes}
                    overallComment={result.overallComment}
                    analysisContainerRef={analysisContainerRef}
                />
            </Box>
        </Box>
        <EditablePromptSection
          issues={result.detailedIssues}
          showToast={showToast}
          onImprovePrompt={onImprovePrompt}
          isImproving={isImproving}
          improvedPrompt={improvedPrompt}
          onReanalyze={onReanalyze}
        />
    </Box>
  );
}

export default AnalysisResults;
